import { useQuery } from "react-query";
import { sureApiCall } from "src/api/axiosClient/sureApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { isQueryReady } from "src/api/axiosClient/utils";
import i18next from "i18next";
import { AxiosError } from "axios";
import { z } from "zod";

export const getOneSsqApiKey = "getOneSsq" as const;

type Options = {
    enabled?: boolean;
};

export const useGetOneSsq = (ssqId: number | undefined, options?: Options) => {
    const { enabled = true } = options || {};

    const isEnabled = ssqId !== undefined && enabled;
    const query = useQuery({
        cacheTime: 300000, // Cache for 5 minutes instead of 0
        staleTime: 180000, // Consider data fresh for 3 minutes
        enabled: isEnabled,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        retryOnMount: false,
        retry: (failureCount: number, error: unknown) => {
            if (ssqId === undefined) {
                return false;
            }

            const status = (error as AxiosError).response?.status;
            if (status === 404) {
                return false;
            }

            return failureCount < 1;
        },
        queryKey: [getOneSsqApiKey, ssqId, i18next.resolvedLanguage],
        queryFn: async () => {
            if (!ssqId) {
                return;
            }

            return await sureApiCall.get(`/api/ssqs/${ssqId}`);
        },
        select: (response) => {
            if (!response) {
                return;
            }

            const ssq = response.data;
            return zodParseWithLog(ssq, SsqSchema, getOneSsqApiKey);
        },
    });

    const isReady = isQueryReady(query, isEnabled);

    return {
        ssq: query.data,
        isReady,
        ...query,
    };
};

const BaseSsqScoreSchema = z.object({
    id: z.number(),
    displayMode: z.string().optional(),
});

export type SsqScore = z.infer<typeof BaseSsqScoreSchema> & {
    children?: SsqScore[];
};

export const SsqScoreSchema: z.ZodType<SsqScore> = BaseSsqScoreSchema.extend({
    children: z.lazy(() => SsqScoreSchema.array().optional()),
});

const VersionSchema = z.object({
    id: z.number(),
    versionMajor: z.number(),
    versionMinor: z.number(),
    status: z.number(),
    currentVersion: z.boolean(),
    releaseNotes: z.string().optional(),
    referenceMetadata: z.string(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
});

export type Version = z.infer<typeof VersionSchema>;

export const SsqSchema = z.object({
    id: z.number(),
    title: z.string(),
    displayTitle: z.string().nullable().optional(),
    monolithic: z.boolean(),
    allowComments: z.boolean(),
    scores: z.array(SsqScoreSchema),
    availableScores: z.array(SsqScoreSchema),
    minimumCompletionRate: z.number().min(0).max(100),
    currentVersion: VersionSchema,
    versions: VersionSchema.array(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
    createdBy: z.object({ firstname: z.string(), lastname: z.string() }).optional(),
    updatedBy: z.object({ firstname: z.string(), lastname: z.string() }).optional(),
});

export type Ssq = z.infer<typeof SsqSchema>;
